import SelectableTile from '@carvana/ds-selectable-tile';
import omit from 'lodash.omit';
import { useContext, useState } from 'react';
import {
  CardImage,
  HeadlineRow,
  ImageContainer,
  ImageUnavailable,
  PriceContainer,
  PriceLabel,
  PriceValue,
  StockCount,
  Title
} from '@/new-cars/common/ConfiguratorCard.styles';
import tileStyles from '@/new-cars/common/SelectableTile.module.css';
import ParentModelContext from '~/context/new-cars/ParentModelContext';
import type { DoorsYearResult } from '~/contracts/new-cars';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';
import { isFallbackImage } from '~/utilities/isFallbackImage';
import optimizeCardImage from '~/utilities/new-cars/optimizeCardImage';
import { Content } from './DoorsCard.styles';

type OwnProps = {
  disabled?: boolean;
  door: DoorsYearResult;
  selected: boolean;
};

export default function DoorsCard({
  disabled = false,
  door,
  selected
}: OwnProps) {
  const { setSelectedDoors } = useContext(ParentModelContext);
  const [isImageBroken, setIsImageBroken] = useState(false);

  const { count, description, imageUrl, lowestPrice, title } = door;
  const isFallback = isFallbackImage(imageUrl);
  const optimizedImageUrl = optimizeCardImage(imageUrl, isFallback);

  return (
    <SelectableTile
      disabled={disabled}
      selected={selected}
      onClick={() => {
        setSelectedDoors(omit({ ...door, ...door.next }, 'next'));
      }}
      data-analytics-attributes={JSON.stringify({
        level: 'Doors',
        section: 'Doors',
        text: title,
        tileAction: selected ? 'selected' : 'deselected'
      })}
      data-analytics-id="New Cars"
      data-testid="doors-card"
      value={title}
      variant="simple-select"
      className={tileStyles.tile}
    >
      <ImageContainer disabled={disabled} isFallback={isFallback}>
        {optimizedImageUrl && !isImageBroken ? (
          <CardImage
            alt={description}
            data-testid="doors-card-image"
            onError={() => setIsImageBroken(true)}
            src={optimizedImageUrl}
          />
        ) : (
          <ImageUnavailable>Image unavailable</ImageUnavailable>
        )}
      </ImageContainer>
      <Content>
        <HeadlineRow>
          <Title disabled={disabled}>{title}</Title>
          <PriceContainer data-testid="doors-card-price">
            <PriceLabel>from </PriceLabel>
            <PriceValue disabled={disabled}>
              ${formatAndDropDecimal(lowestPrice)}
            </PriceValue>
          </PriceContainer>
        </HeadlineRow>
        <StockCount data-testid="doors-card-stock-count">
          {count} in stock
        </StockCount>
      </Content>
    </SelectableTile>
  );
}
